/* eslint-disable */
import deepmerge from 'deepmerge';

import axios from 'axios';

import { asyncApi, handleError } from '@/shared/services/api';
import baseApi from '@/shared/services/bright_finder';
import assignment from '@/specialist/services/api/assignment';
import attachment from '@/specialist/services/api/attachment';
import claim from '@/specialist/services/api/claim';
import complaint from '@/specialist/services/api/complaint';
import course from '@/specialist/services/api/course';
import customView from '@/specialist/services/api/custom-view';
import customViewResult from '@/specialist/services/api/custom-view-result';
import eligibilityProgram from '@/specialist/services/api/eligibility-program';
import enrollment from '@/specialist/services/api/enrollment';
import eventAlertConfiguration from '@/specialist/services/api/event-alert-configuration';
import familySubsidy from '@/specialist/services/api/family-subsidy';
import familySubsidyCustomView from '@/specialist/services/api/family-subsidy-custom-view';
import familySubsidyInvitation from '@/specialist/services/api/family-subsidy-invitation';
import familySubsidyProgram from '@/specialist/services/api/family-subsidy-program';
import form from '@/specialist/services/api/form';
import formCustomView from '@/specialist/services/api/form-custom-view';
import fundingSource from '@/specialist/services/api/funding-source';
import grantProgram from '@/specialist/services/api/grant_program';
import group from '@/specialist/services/api/group';
import householdMember from '@/specialist/services/api/household-member';
import iepReferral from '@/specialist/services/api/iep-referral';
import lesson from '@/specialist/services/api/lesson';
import lessonCompletion from '@/specialist/services/api/lesson-completion';
import linkedRecord from '@/specialist/services/api/linked-record';
import member from '@/specialist/services/api/member';
import note from '@/specialist/services/api/note';
import provider from '@/specialist/services/api/provider';
import publicSchema from '@/specialist/services/api/public-schema';
import report from '@/specialist/services/api/report';
import reportDataType from '@/specialist/services/api/report-data-type';
import reportFolder from '@/specialist/services/api/report-folder';
import review from '@/specialist/services/api/review';
import reviewer from '@/specialist/services/api/reviewer';
import schema from '@/specialist/services/api/schema';
import screenerProgram from '@/specialist/services/api/screener-program';
import site from '@/specialist/services/api/site';
import subsidy from '@/specialist/services/api/subsidy';
import subsidyAward from '@/specialist/services/api/subsidy-award';
import subsidyProgram from '@/specialist/services/api/subsidy-program';
import summaryConfiguration from '@/specialist/services/api/summary-configuration';
import taskType from '@/specialist/services/api/task-type';
import team from '@/specialist/services/api/team';
import termsAgreement from '@/specialist/services/api/terms-agreement';
import webpage from '@/specialist/services/api/webpage';
import schemaFunction from '@/specialist/services/api/schema-function';
import schemaFunctionTest from '@/specialist/services/api/schema-function-test';
import schemaFunctionTestSuiteRun from '@/specialist/services/api/schema-function-test-suite-run';
import schemaShare from '@/specialist/services/api/schema-share';
import { denormalizeResponse } from '@/specialist/services/api/json-api';

const organizationApi = {
  organization: {
    baseUrl() {
      return `${window.api_url}/admin/organization`;
    },

    get(success, failure) {
      if (!success) return axios.get(this.baseUrl());
      return axios
        .get(this.baseUrl())
        .then(success)
        .catch(failure || handleError);
    },

    update(organization, success) {
      if (!success) return axios.patch(this.baseUrl(), { organization }).catch(handleError);

      return axios.patch(this.baseUrl(), { organization }).then(success).catch(handleError);
    },

    accessRequest: asyncApi({
      endpoint: '/organization/access_requests',
      payloadKey: 'access_request',
    }),

    ageGroup: asyncApi({ endpoint: '/organization/age_groups', payloadKey: 'age_group' }),

    agreement: {
      baseUrl(id = null) {
        let url = `${window.api_url}/organization/agreements`;
        if (id) url += `/${id}`;
        return url;
      },

      createPayload(value) {
        return { agreement: value };
      },

      create(agreement, success, failure) {
        if (!success) return axios.post(this.baseUrl(), { agreement }).catch(handleError);
        axios.post(this.baseUrl(), { agreement }).then(success).catch(failure);
      },

      destroy(id, success) {
        axios.delete(this.baseUrl(id)).then(success).catch(handleError);
      },

      downloadUrl(id, filename) {
        return `${this.baseUrl(id)}.pdf?filename=${encodeURIComponent(filename)}.pdf`;
      },

      index(filters, success) {
        if (!success) return axios.get(this.baseUrl(), { params: filters });
        axios.get(this.baseUrl(), { params: filters }).then(success);
      },

      async get(id, params = {}, successCallback = null, failureCallback = null) {
        return axios
          .get(this.baseUrl(id), { params })
          .then((response) => {
            if (successCallback) successCallback(response);
            return response;
          })
          .catch((error) => {
            if (failureCallback) return failureCallback(error);
            return handleError(error);
          });
      },

      async update(id, updateObject, params = {}, successCallback = null, failureCallback = null) {
        return axios
          .patch(this.baseUrl(id), this.createPayload(updateObject), { params })
          .then((response) => {
            if (successCallback) successCallback(response);
            return response;
          })
          .catch((error) => {
            if (failureCallback) return failureCallback(error);
            return handleError(error);
          });
      },
    },

    announcement: asyncApi({
      endpoint: '/organization/announcements',
      payloadKey: 'announcement',
    }),

    assessment: {
      ...asyncApi({
        endpoint: '/organization/assessments',
        payloadKey: 'assessment',
      }),

      downloadUrl(id, filename) {
        return `${this.baseUrl(id)}.pdf?filename=${encodeURIComponent(filename)}.pdf`;
      },
    },

    asset: {
      baseUrl() {
        return `${window.api_url}/organization/assets`;
      },

      create(asset, success, failure) {
        axios.post(this.baseUrl(), { asset }).then(success).catch(failure);
      },

      destroy(id, success) {
        axios.delete(`${this.baseUrl()}/${id}`).then(success);
      },

      index(params, success) {
        axios.get(this.baseUrl(), { params }).then(success);
      },

      get(id, success) {
        axios.get(`${this.baseUrl()}/${id}`).then(success);
      },

      update(id, asset, success, failure) {
        axios.patch(`${this.baseUrl()}/${id}`, { asset }).then(success).catch(failure);
      },
    },

    assignment,

    assignmentCompletion: asyncApi({
      endpoint: '/organization/assignment_completions',
      payloadKey: 'assignment_completion',
    }),

    assignment_configuration: {
      baseUrl() {
        return `${window.api_url}/admin/assignment_configurations`;
      },

      create(assignmentConfiguration) {
        return axios
          .post(this.baseUrl(), { assignment_configuration: assignmentConfiguration })
          .catch(handleError);
      },

      destroy(assignmentConfiguration) {
        return axios.delete(`${this.baseUrl()}/${assignmentConfiguration.id}`);
      },

      index(filters) {
        return axios.get(this.baseUrl(), { params: filters });
      },

      update(assignmentConfiguration) {
        return axios
          .patch(`${this.baseUrl()}/${assignmentConfiguration.id}`, {
            assignment_configuration: assignmentConfiguration,
          })
          .catch(handleError);
      },
    },

    attachment,

    backgroundCheck: asyncApi({
      endpoint: '/organization/background_checks',
      payloadKey: 'background_check',
    }),

    balanceAccount: asyncApi({
      endpoint: '/organization/balance_account',
    }),

    balanceTransfer: asyncApi({
      endpoint: '/organization/balance_transfers',
      payloadKey: 'balance_transfer',
    }),

    bankAccount: asyncApi({
      endpoint: '/organization/bank_accounts',
      payloadKey: 'bank_account',
    }),

    billingStatements: {
      ...asyncApi({
        endpoint: '/organization/billing_statements',
        payloadKey: 'billing_statement',
      }),

      async retry(id, successCallback = null, failureCallback = null) {
        return axios
          .patch(`${this.baseUrl(id)}/retry`, this.createPayload({ id }))
          .then((response) => {
            if (successCallback) successCallback(response);
            return response;
          })
          .catch((error) => {
            if (failureCallback) failureCallback(error);
            return handleError(error);
          });
      },
    },

    business: asyncApi({
      endpoint: '/organization/businesses',
      payloadKey: 'business',
    }),

    claim,

    complaint,

    course,

    course_completion: {
      baseUrl(id = null) {
        let url = `${window.api_url}/organization/course_completions`;
        if (id) url += `/${id}`;
        return url;
      },

      create(resource, success, failure) {
        axios.post(this.baseUrl(), { course_completion: resource }).then(success).catch(failure);
      },

      destroy(id, success) {
        axios.delete(this.baseUrl(id)).then(success);
      },

      downloadUrl(id, filename) {
        const siteId = window.site_id || '';
        return `${this.baseUrl(id)}.pdf?filename=${encodeURIComponent(filename)}.pdf&site_id=${siteId}`;
      },

      index(filters, success) {
        if (!success) return axios.get(this.baseUrl(), { params: filters });

        return axios.get(this.baseUrl(), { params: filters }).then(success);
      },

      get(id, success) {
        axios.get(this.baseUrl(id)).then(success);
      },

      update(id, resource, success, failure) {
        axios.patch(this.baseUrl(id), { course_completion: resource }).then(success).catch(failure);
      },
    },

    contentTemplate: {
      baseUrl(id = null) {
        let url = `${window.api_url}/organization/content_templates`;
        if (id) url += `/${id}`;
        return url;
      },

      createPayload(value) {
        if (!this.payloadKey) return { data: value };

        return { [this.payloadKey]: value };
      },

      create(attributes, failure) {
        return axios
          .post(this.baseUrl(), {
            data: {
              type: 'content_template',
              attributes,
            },
          })
          .catch(failure);
      },

      index(filters) {
        return axios.get(this.baseUrl(), { params: filters });
      },

      get(id) {
        return axios.get(this.baseUrl(id));
      },

      async update(id, updateObject, params = {}, successCallback = null, failureCallback = null) {
        return axios
          .patch(this.baseUrl(id), this.createPayload(updateObject), { params })
          .then((response) => {
            if (successCallback) successCallback(response);
            return response;
          })
          .catch((error) => {
            if (failureCallback) return failureCallback(error);
            return handleError(error);
          });
      },

      async destroy(id, successCallback = null, failureCallback = null) {
        return axios
          .delete(this.baseUrl(id))
          .then((response) => {
            if (successCallback) successCallback(response);
            return response;
          })
          .catch((error) => {
            if (failureCallback) return failureCallback(error);
            return handleError(error);
          });
      },
    },

    custom_view: customView,

    customViewResult,

    dashboard: asyncApi({
      endpoint: '/organization/dashboards',
      payloadKey: 'dashboard',
    }),

    fundsTransfer: asyncApi({
      endpoint: '/organization/funds_transfers',
      payloadKey: 'funds_transfer',
    }),

    dwolla: {
      onDemandAuthorization: {
        async create() {
          return axios.post(`${window.api_url}/dwolla/on_demand_authorizations`).catch(handleError);
        },
      },
    },

    eligibilityProgram,
    eventAlertConfiguration,
    enrollment,
    familySubsidyCustomView,
    family_subsidy: familySubsidy,
    family_subsidy_program: familySubsidyProgram,
    familySubsidyInvitation,
    favorite: {
      baseUrl() {
        return `${window.api_url}/organization/favorites`;
      },

      create(favorite, success) {
        if (!success) return axios.post(this.baseUrl(), { favorite }).catch(handleError);
        return axios.post(this.baseUrl(), { favorite }).then(success);
      },

      destroy(id, success) {
        if (!success) return axios.delete(`${this.baseUrl()}/${id}`).catch(handleError);
        return axios.delete(`${this.baseUrl()}/${id}`).then(success);
      },

      index(filters, success) {
        if (!success) return axios.get(this.baseUrl(), { params: filters }).catch(handleError);
        return axios.get(this.baseUrl(), { params: filters }).then(success);
      },
    },

    form,

    formCustomView,

    fund: asyncApi({
      endpoint: '/organization/funds',
      payloadKey: 'fund',
    }),

    funding_source: fundingSource,

    fundsTransfer: asyncApi({
      endpoint: '/organization/funds_transfers',
      payloadKey: 'funds_transfer',
    }),

    funds_transfer: {
      baseUrl() {
        return `${window.api_url}/organization/funds_transfers`;
      },

      create(fundsTransfer, success, failure) {
        axios.post(this.baseUrl(), { funds_transfer: fundsTransfer }).then(success).catch(failure);
      },

      createOrUpdate(fundsTransfer, success, failure) {
        if (fundsTransfer.id) {
          this.update(fundsTransfer.id, fundsTransfer, success, failure);
        } else {
          this.create(fundsTransfer, success, failure);
        }
      },

      update(id, fundsTransfer, success, failure) {
        axios
          .patch(`${this.baseUrl()}/${id}`, { funds_transfer: fundsTransfer })
          .then(success)
          .catch(failure);
      },
    },

    grant: {
      ...asyncApi({
        endpoint: '/organization/grants',
        payloadKey: 'grant',
      }),

      downloadUrl(id, filename) {
        return `${this.baseUrl(id)}.pdf?filename=${encodeURIComponent(filename)}.pdf`;
      },
    },

    grant_award: {
      baseUrl(id) {
        if (id) {
          return `${window.api_url}/organization/grant_awards/${id}`;
        }
        return `${window.api_url}/organization/grant_awards`;
      },

      create(grant_award, success, failure) {
        axios.post(this.baseUrl(), { grant_award }).then(success).catch(failure);
      },

      destroy(id, success) {
        axios.delete(this.baseUrl(id)).then(success);
      },

      get(id, success) {
        axios.get(this.baseUrl(id)).then(success);
      },

      index(filters, success) {
        axios.get(this.baseUrl(), { params: filters }).then(success);
      },

      update(id, grant_award, success, failure) {
        axios.patch(this.baseUrl(id), { grant_award }).then(success).catch(failure);
      },
    },

    groupActivityType: asyncApi({
      endpoint: '/organization/group_activity_types',
      payloadKey: 'group_activity_type',
    }),

    householdMember,

    iep_referral: iepReferral,

    internal_file: {
      baseUrl() {
        return `${window.api_url}/organization/internal_files`;
      },

      get(id) {
        return axios.get(`${this.baseUrl()}/${id}`);
      },
    },

    job_config: {
      baseUrl() {
        return `${window.api_url}/organization/job_configs`;
      },

      create(attributes, content_template_id, site_id) {
        return axios
          .post(this.baseUrl(), {
            data: {
              type: 'job_config',
              attributes,
              relationships: {
                content_template: {
                  data: {
                    type: 'content_template',
                    id: content_template_id,
                  },
                },
                site: {
                  data: {
                    type: 'site',
                    id: site_id,
                  },
                },
              },
            },
          })
          .catch(handleError);
      },

      destroy(id) {
        return axios.delete(`${this.baseUrl()}/${id}`);
      },

      index(filters) {
        return axios.get(this.baseUrl(), { params: filters });
      },

      get(id) {
        return axios.get(`${this.baseUrl()}/${id}`);
      },

      update(id, attributes, content_template_id, site_id) {
        return axios
          .patch(`${this.baseUrl()}/${id}`, {
            data: {
              type: 'job_config',
              attributes,
              relationships: {
                content_template: {
                  data: {
                    type: 'content_template',
                    id: content_template_id,
                  },
                },
                site: {
                  data: {
                    type: 'site',
                    id: site_id,
                  },
                },
              },
            },
          })
          .catch(handleError);
      },

      preview(id, phoneNumber) {
        const url = `${this.baseUrl()}/${id}/preview`;
        const data = {
          type: 'job_config',
          attributes: {
            phone: phoneNumber,
          },
        };

        return axios.post(url, { data }).catch(handleError);
      },
    },

    job_run: {
      baseUrl() {
        return `${window.api_url}/organization/job_runs`;
      },

      create(attributes, job_config_id) {
        return axios
          .post(this.baseUrl(), {
            data: {
              type: 'job_run',
              attributes,
              relationships: {
                job_config: {
                  data: {
                    type: 'job_config',
                    id: job_config_id,
                  },
                },
              },
            },
          })
          .catch(handleError);
      },

      index(filters) {
        return axios.get(this.baseUrl(), { params: filters });
      },
    },

    jsonWebToken: asyncApi({
      endpoint: '/organization/json_web_tokens',
      payloadKey: 'json_web_token',
    }),

    lesson,

    lessonCompletion,

    license: asyncApi({
      endpoint: '/organization/licenses',
      payloadKey: 'license',
    }),

    licensingProgram: asyncApi({
      endpoint: '/organization/licensing_programs',
      payloadKey: 'licensing_program',
    }),

    linkedRecord,

    localized_content_template: {
      baseUrl() {
        return `${window.api_url}/organization/localized_content_templates`;
      },

      create(attributes, content_template_id) {
        return axios
          .post(this.baseUrl(), {
            data: {
              type: 'localized_content_template',
              attributes,
              relationships: {
                content_template: {
                  data: {
                    type: 'content_template',
                    id: content_template_id,
                  },
                },
              },
            },
          })
          .catch(handleError);
      },

      destroy(id) {
        return axios.delete(`${this.baseUrl()}/${id}`);
      },

      index(filters) {
        return axios.get(this.baseUrl(), { params: filters });
      },

      get(id) {
        return axios.get(`${this.baseUrl()}/${id}`);
      },

      update(id, attributes) {
        return axios
          .patch(`${this.baseUrl()}/${id}`, {
            data: {
              type: 'localized_content_template',
              attributes,
            },
          })
          .catch(handleError);
      },
    },

    metric: {
      baseUrl(id) {
        if (id) {
          return `${window.api_url}/organization/metrics/${id}`;
        }
        return `${window.api_url}/organization/metrics`;
      },

      create(metric, success, failure) {
        axios.post(this.baseUrl(), metric).then(success).catch(failure);
      },

      destroy(id, success) {
        axios.delete(this.baseUrl(id)).then(success);
      },

      get(id, success) {
        axios.get(this.baseUrl(id)).then(success);
      },

      index(filters, success) {
        axios.get(this.baseUrl(), { params: filters }).then(success);
      },

      update(id, metric, success, failure) {
        axios.patch(this.baseUrl(id), { metric }).then(success).catch(failure);
      },
    },

    schema,

    group,

    group_communications: asyncApi({
      endpoint: '/organization/group_communications',
    }),

    grant_program: grantProgram,

    invoice: {
      baseUrl() {
        return `${window.api_url}/organization/invoices`;
      },

      downloadUrl(filters) {
        // eslint-disable-next-line no-unused-vars
        const params = Object.fromEntries(Object.entries(filters).filter(([_k, v]) => v != null));
        params.filename = `Invoices Export ${new Date().toString()}.csv`;
        return [`${this.baseUrl()}.csv`, new URLSearchParams(params).toString()].join('?');
      },

      index(filters) {
        return axios.get(this.baseUrl(), { params: filters });
      },

      get(id) {
        return axios.get(`${this.baseUrl()}/${id}`);
      },

      update(id, invoice, success, failure) {
        if (success) {
          return axios.patch(`${this.baseUrl()}/${id}`, { invoice }).then(success).catch(failure);
        }

        return axios.patch(`${this.baseUrl()}/${id}`, { invoice }).catch(handleError);
      },
    },

    invoice_line_item: {
      baseUrl() {
        return `${window.api_url}/organization/invoice_line_items`;
      },

      create(invoiceLineItem, success, failure) {
        const params = { invoice_line_item: invoiceLineItem };
        if (!success) return axios.post(this.baseUrl(), params).catch(handleError);

        return axios.post(this.baseUrl(), params).then(success).catch(failure);
      },

      createOrUpdate(invoiceLineItem, success, failure) {
        if (invoiceLineItem.id) {
          return this.update(invoiceLineItem.id, invoiceLineItem, success, failure);
        } else {
          return this.create(invoiceLineItem, success, failure);
        }
      },

      destroy(id, success) {
        if (!success) return axios.delete(`${this.baseUrl()}/${id}`).catch(handleError);

        return axios.delete(`${this.baseUrl()}/${id}`).then(success);
      },

      index(filters, success) {
        if (!success) return axios.get(this.baseUrl(), { params: filters }).catch(handleError);

        return axios.get(this.baseUrl(), { params: filters }).then(success);
      },

      get(id, success) {
        if (!success) return axios.get(`${this.baseUrl()}/${id}`).catch(handleError);

        return axios.get(`${this.baseUrl()}/${id}`).then(success);
      },

      update(id, invoiceLineItem, success, failure) {
        const params = { invoice_line_item: invoiceLineItem };
        const url = `${this.baseUrl()}/${id}`;

        if (!success) return axios.patch(url, params).catch(handleError);

        return axios.patch(url, params).then(success).catch(failure);
      },
    },

    label: {
      baseUrl(id) {
        if (id) {
          return `${window.api_url}/organization/labels/${id}`;
        }
        return `${window.api_url}/organization/labels`;
      },

      create(params) {
        return axios.post(this.baseUrl(), params).catch(handleError);
      },

      destroy(labelId) {
        return axios.delete(this.baseUrl(labelId)).catch(handleError);
      },

      index(filters) {
        return axios.get(this.baseUrl(), { params: filters }).catch(handleError);
      },

      update(id, label) {
        return axios.patch(this.baseUrl(id), { label }).catch(handleError);
      },
    },

    label_assignment: {
      baseUrl() {
        return `${window.api_url}/organization/label_assignments`;
      },

      create(assignment) {
        return axios.post(this.baseUrl(), { label_assignment: assignment });
      },

      bulk_assign(filters) {
        return axios.patch(this.baseUrl(), filters);
      },

      destroy(filters) {
        return axios.delete(this.baseUrl(), { data: filters });
      },
    },

    list: {
      baseUrl(listId) {
        if (listId) {
          return `${window.api_url}/organization/lists/${listId}`;
        }
        return `${window.api_url}/organization/lists`;
      },

      create(list, success) {
        if (!success) return axios.post(this.baseUrl(), { list }).catch(handleError);
        return axios.post(this.baseUrl(), { list }).then(success);
      },

      destroy(listId, success) {
        if (!success) return axios.delete(this.baseUrl(listId)).catch(handleError);
        return axios.delete(this.baseUrl(listId)).then(success);
      },

      get(listId, success) {
        axios.get(this.baseUrl(listId)).then(success);
      },

      index(filters, success) {
        if (!success) return axios.get(this.baseUrl(), { params: filters }).catch(handleError);
        return axios.get(this.baseUrl(), { params: filters }).then(success);
      },

      update(listId, list, success) {
        axios.patch(this.baseUrl(listId), { list }).then(success);
      },
    },

    login_code: {
      baseUrl() {
        return `${window.api_url}/organization/login_codes`;
      },

      create(resource, success, failure) {
        axios.post(this.baseUrl(), { login_code: resource }).then(success).catch(failure);
      },
    },

    member,

    messageTemplate: {
      baseUrl(id = null) {
        let url = `${window.api_url}/organization/message_templates`;
        if (id) url += `/${id}`;
        return url;
      },

      create(params, success, failure) {
        axios.post(this.baseUrl(), { message_template: params }).then(success).catch(failure);
      },

      destroy(id, success) {
        axios.delete(`${this.baseUrl()}/${id}`).then(success);
      },

      async index(params = {}, successCallback = null, failureCallback = null) {
        return axios
          .get(this.baseUrl(), { params })
          .then((response) => {
            if (successCallback) successCallback(response);
            return response;
          })
          .catch((error) => {
            if (failureCallback) return failureCallback(error);
            return handleError(error);
          });
      },

      get(id, success) {
        axios.get(`${this.baseUrl()}/${id}`).then(success);
      },

      htmlPreviewUrl(id, lang) {
        return `${this.baseUrl()}/${id}.html?lang=${lang}`;
      },

      update(id, message_template, success, failure) {
        axios.patch(`${this.baseUrl()}/${id}`, { message_template }).then(success).catch(failure);
      },
    },

    message: {
      baseUrl() {
        return `${window.api_url}/organization/messages`;
      },

      create(message, success, failure) {
        if (!success) return axios.post(this.baseUrl(), { message });

        return axios.post(this.baseUrl(), { message }).then(success).catch(failure);
      },

      get(id, success) {
        if (!success) return axios.get(`${this.baseUrl()}/${id}`);

        return axios.get(`${this.baseUrl()}/${id}`).then(success);
      },

      index(filters, success) {
        if (!success) return axios.get(this.baseUrl(), { params: filters });

        return axios.get(this.baseUrl(), { params: filters }).then(success);
      },
    },

    note,

    ownershipRequest: asyncApi({
      endpoint: '/organization/ownership_requests',
      payloadKey: 'ownership_request',
    }),

    permission: {
      baseUrl() {
        return `${window.api_url}/organization/permissions`;
      },

      create(permission, success, failure) {
        axios.post(this.baseUrl(), { permission }).then(success).catch(failure);
      },

      destroy(id, success) {
        axios.delete(`${this.baseUrl()}/${id}`).then(success);
      },

      index(filters, success) {
        axios.get(this.baseUrl(), { params: filters }).then(success);
      },

      get(id, success) {
        axios.get(`${this.baseUrl()}/${id}`).then(success);
      },

      update(id, permission, success, failure) {
        axios.patch(`${this.baseUrl()}/${id}`, { permission }).then(success).catch(failure);
      },
    },

    profile: asyncApi({
      endpoint: '/organization/profile',
      payloadKey: 'profile',
      selfWithCredentials: true,
    }),

    program: asyncApi({
      endpoint: '/v1/programs',
      payloadKey: 'program',
    }),

    provider,

    public_schema: publicSchema,

    report,
    reportDataType,
    reportFolder,
    review,
    reviewer,

    revision: {
      baseUrl() {
        return `${window.api_url}/organization/revisions`;
      },

      index(filters, success) {
        if (!success) return axios.get(this.baseUrl(), { params: filters });

        return axios.get(this.baseUrl(), { params: filters }).then(success);
      },

      aggregatedIndex(filters) {
        return axios
          .get(`${this.baseUrl()}/aggregated_index`, { params: filters })
          .catch(handleError);
      },

      show(revisionId, success) {
        axios.get(`${this.baseUrl()}/${revisionId}`).then(success);
      },

      update(revisionId, revision, success) {
        axios.patch(`${this.baseUrl()}/${revisionId}`, { revision }).then(success);
      },
    },

    revision_view: {
      baseUrl() {
        return `${window.api_url}/organization/revision_views`;
      },

      create(resource, success, failure) {
        axios.post(this.baseUrl(), { revision_view: resource }).then(success).catch(failure);
      },

      destroy(id, success) {
        axios.delete(`${this.baseUrl()}/${id}`).then(success);
      },

      index(params, success) {
        axios.get(this.baseUrl(), { params }).then(success);
      },

      get(id, success) {
        axios.get(`${this.baseUrl()}/${id}`).then(success);
      },

      update(id, resource, success, failure) {
        axios
          .patch(`${this.baseUrl()}/${id}`, { revision_view: resource })
          .then(success)
          .catch(failure);
      },
    },

    roomSchedule: asyncApi({
      endpoint: '/organization/room_schedules',
      payloadKey: 'room_schedule',
    }),

    screenerProgram,

    search: {
      baseUrl(searchId) {
        if (searchId) {
          return `${window.api_url}/organization/searches/${searchId}`;
        }
        return `${window.api_url}/organization/searches`;
      },

      create(search, success, error) {
        if (!success) return axios.post(this.baseUrl(), { search }).catch(handleError);
        return axios.post(this.baseUrl(), { search }).then(success).catch(error);
      },

      get(id, success) {
        axios.get(this.baseUrl(id)).then(success);
      },

      index(success, failure) {
        axios.get(this.baseUrl()).then(success).catch(failure);
      },

      match: {
        baseUrl(searchId) {
          return `${window.api_url}/organization/searches/${searchId}/matches`;
        },

        index(searchId, params, success) {
          if (!success) return axios.get(this.baseUrl(searchId), { params }).catch(handleError);
          return axios.get(this.baseUrl(searchId), { params }).then(success);
        },
      },

      update(id, search, success, failure) {
        if (!success) return axios.patch(this.baseUrl(id), { search }).catch(handleError);
        return axios.patch(this.baseUrl(id), { search }).then(success).catch(failure);
      },
    },

    schemaFunction,
    schemaFunctionTest,
    schemaFunctionTestSuiteRun,
    schemaShare,

    seat: {
      baseUrl() {
        return `${window.api_url}/organization/seats`;
      },

      create(seat) {
        return axios.post(this.baseUrl(), { seat });
      },

      destroy(seatId) {
        return axios.delete(`${this.baseUrl()}/${seatId}`);
      },

      index(subsidyProgramId, programIds) {
        return axios.get(
          `${this.baseUrl(subsidyProgramId)}?subsidy_program_id=${subsidyProgramId}&program_ids=${programIds}`,
        );
      },

      update(seat) {
        return axios.patch(`${this.baseUrl()}/${seat.id}`, { seat });
      },

      bulkUpdate(seatCountParams) {
        return axios.post(`${this.baseUrl()}/bulk_update`, seatCountParams).catch(handleError);
      },
    },

    sent_email: asyncApi({
      endpoint: '/organization/sent_emails',
    }),

    sent_sms: asyncApi({
      endpoint: '/organization/sent_sms',
    }),

    site,

    specialistRole: asyncApi({
      endpoint: '/organization/specialist_roles',
      payloadKey: 'specialist_role',
    }),

    subsidy,

    subsidy_award: subsidyAward,

    subsidy_program: subsidyProgram,

    summary_configuration: summaryConfiguration,

    survey: {
      baseUrl(id) {
        const base = `${window.api_url}/organization/surveys`;
        return id ? `${base}/${id}` : base;
      },

      destroy(id) {
        return axios.delete(this.baseUrl(id));
      },

      index(filters) {
        return axios.get(this.baseUrl(), { params: filters });
      },

      get(id) {
        return axios.get(this.baseUrl(id));
      },
    },

    task: asyncApi({
      endpoint: '/organization/tasks',
      payloadKey: 'task',
    }),

    helpDocument: {
      ...asyncApi({
        endpoint: '/organization/help_documents',
        payloadKey: 'help_document',
      }),
      async acceptProposal(id) {
        if (!id) return handleError(new Error('ID is required'));
        return axios.post(`${this.baseUrl(id)}/accept_proposal`).catch(handleError);
      },
      async rejectProposal(id) {
        if (!id) return handleError(new Error('ID is required'));
        return axios.post(`${this.baseUrl(id)}/reject_proposal`).catch(handleError);
      },
    },
    taskType,

    team,

    teamMembership: asyncApi({
      endpoint: '/organization/team_memberships',
      payloadKey: 'team_membership',
    }),

    termsAgreement,

    translation: {
      url(id) {
        if (id) {
          return `${window.api_url}/organization/translations/${id}`;
        }
        return `${window.api_url}/organization/translations`;
      },

      create(translation, success, failure) {
        return axios.post(this.url(), translation).catch(handleError);
      },

      destroy(translationId, success) {
        axios.delete(this.url(translationId)).then(success);
      },

      downloadUrl(filename) {
        return `${this.url()}.csv?filename=${filename}`;
      },

      get(translationId, success) {
        axios.get(this.url(translationId)).then(success);
      },

      index(params, success) {
        axios.get(this.url(), { params }).then(success);
      },

      update(id, translation) {
        return axios.patch(this.url(id), { translation }).catch(handleError);
      },

      upload: {
        baseUrl() {
          return `${window.api_url}/organization/translations/uploads`;
        },

        create(upload) {
          return axios.post(this.baseUrl(), { upload }).catch(handleError);
        },
      },
    },

    upload: {
      baseUrl() {
        return `${window.api_url}/organization/uploads`;
      },

      downloadContentUrl(id) {
        return `${this.baseUrl()}/${id}/download_content`;
      },

      errorReportUrl(id, dryRun = false) {
        const query = dryRun ? '?dry_run=true' : '';

        return `${this.baseUrl()}/${id}/error_report${query}`;
      },

      dryRunContentUrl(id) {
        return `${this.baseUrl()}/${id}/dry_run_content`;
      },

      create(upload, success, failure) {
        axios.post(this.baseUrl(), { upload }).then(success).catch(failure);
      },

      destroy(id, success) {
        axios.delete(`${this.baseUrl()}/${id}`).then(success);
      },

      get(id) {
        return axios.get(`${this.baseUrl()}/${id}`);
      },

      index(params) {
        return axios.get(this.baseUrl(), { params });
      },

      run(id) {
        return axios.patch(`${this.baseUrl()}/${id}/run`);
      },

      dryRun(id) {
        return axios.patch(`${this.baseUrl()}/${id}/dry_run`);
      },

      cancelDryRun(id) {
        return axios.patch(`${this.baseUrl()}/${id}/cancel_dry_run`);
      },

      update(id, upload, success, failure) {
        axios.patch(`${this.baseUrl()}/${id}`, { upload }).then(success).catch(failure);
      },
    },

    vault: {
      baseUrl(id) {
        let url = `${window.api_url}/organization/vaults`;
        if (id) {
          url += `/${id}`;
        }
        return url;
      },

      create(vault, success, failure) {
        axios.post(this.baseUrl(), { vault }).then(success).catch(failure);
      },

      get(id, success) {
        axios.get(this.baseUrl(id)).then(success);
      },

      index(filters, success) {
        axios.get(this.baseUrl(), { params: filters }).then(success);
      },

      update(id, vault, success) {
        return axios.patch(this.baseUrl(id), { vault }).then(success);
      },

      promiseUpdate(id, vault) {
        return axios.patch(this.baseUrl(id), { vault });
      },
    },

    waitlistOffer: asyncApi({
      endpoint: '/organization/wait_list_offers/wait_list_offers',
      payloadKey: 'wait_list_offer',
    }),

    webpage,

    workflowInstance: asyncApi({
      endpoint: '/organization/workflow_instances',
      payloadKey: 'workflow_instance',
    }),

    workflowInstanceSummary: (() => {
      const summariesApi = asyncApi({
        endpoint: '/organization/workflow_instance_summaries',
        payloadKey: 'workflow_instance',
      });

      return {
        async index(params = {}, successCallback = null, failureCallback = null) {
          try {
            const response = await axios.get(summariesApi.baseUrl(), { params });
            const denormalized = denormalizeResponse(response);
            if (successCallback) successCallback(denormalized);
            return denormalized;
          } catch (error) {
            if (failureCallback) return failureCallback(error);
            return handleError(error);
          }
        },
      };
    })(),

    workflowTemplate: asyncApi({
      endpoint: '/organization/workflow_templates',
      payloadKey: 'workflow_template',
    }),

    workflowArtifactType: {
      async index() {
        return axios
          .get(`${window.api_url}/organization/workflow_artifact_types`)
          .catch(handleError);
      },
    },

    workflowReviewAssignee: asyncApi({
      endpoint: '/organization/workflow_review_assignees',
    }),

    workflowTask: (() => {
      const approveApi = asyncApi({
        endpoint: '/organization/workflow_tasks',
        pathSuffix: '/approve',
      });
      const completeApi = asyncApi({
        endpoint: '/organization/workflow_tasks',
        pathSuffix: '/complete',
      });
      const denyApi = asyncApi({
        endpoint: '/organization/workflow_tasks',
        pathSuffix: '/deny',
      });
      const reopenApi = asyncApi({
        endpoint: '/organization/workflow_tasks',
        pathSuffix: '/reopen',
      });
      const skipApi = asyncApi({
        endpoint: '/organization/workflow_tasks',
        pathSuffix: '/skip',
      });
      const tasksApi = asyncApi({
        endpoint: '/organization/workflow_tasks',
        payloadKey: 'workflow_task',
      });

      return {
        approve(id) {
          return approveApi.update(id);
        },

        bulkAssign(params) {
          const url = `${tasksApi.baseUrl()}/bulk_assign`;
          return axios.patch(url, params).catch(handleError);
        },

        complete(id) {
          return completeApi.update(id);
        },

        deny(id) {
          return denyApi.update(id);
        },

        reopen(id) {
          return reopenApi.update(id);
        },

        skip(id) {
          return skipApi.update(id);
        },

        async index(params = {}, successCallback = null, failureCallback = null) {
          try {
            const response = await axios.get(tasksApi.baseUrl(), { params });
            const denormalized = denormalizeResponse(response);
            if (successCallback) successCallback(denormalized);
            return denormalized;
          } catch (error) {
            if (failureCallback) return failureCallback(error);
            return handleError(error);
          }
        },

        update(id, workflowTask) {
          return tasksApi.update(id, workflowTask);
        },
      };
    })(),

    workflowTemplate: asyncApi({
      endpoint: '/organization/workflow_templates',
      payloadKey: 'workflow_template',
    }),

    workflowTaskTemplate: {
      baseUrl(workflowTemplateId, id) {
        let url = `${window.api_url}/organization/workflow_templates/${workflowTemplateId}/workflow_task_templates`;
        if (id) url += `/${id}`;
        return url;
      },

      async create(workflowTemplateId, workflowTaskTemplate) {
        return axios
          .post(this.baseUrl(workflowTemplateId), { workflow_task_template: workflowTaskTemplate })
          .catch(handleError);
      },

      async update(workflowTemplateId, id, workflowTaskTemplate) {
        return axios
          .patch(this.baseUrl(workflowTemplateId, id), {
            workflow_task_template: workflowTaskTemplate,
          })
          .catch(handleError);
      },
    },

    workflowTriggerTemplate: {
      baseUrl(workflowTemplateId, id) {
        let url = `${window.api_url}/organization/workflow_templates/${workflowTemplateId}/workflow_trigger_templates`;
        if (id) url += `/${id}`;
        return url;
      },

      create(workflowTemplateId, workflowTriggerTemplate) {
        return axios
          .post(this.baseUrl(workflowTemplateId), {
            workflow_trigger_template: workflowTriggerTemplate,
          })
          .catch(handleError);
      },

      update(workflowTemplateId, id, workflowTriggerTemplate) {
        return axios
          .patch(this.baseUrl(workflowTemplateId, id), {
            workflow_trigger_template: workflowTriggerTemplate,
          })
          .catch(handleError);
      },
    },

    // TODO: Rename `event` to `automation` in the JS client and call sites;
    // the HTTP endpoints now live under /organization/automations.
    automation: {
      event: asyncApi({
        endpoint: '/organization/automations',
        payloadKey: 'event',
      }),

      triggerBehaviors: {
        index() {
          return axios.get(`${window.api_url}/organization/automations/automatable_behaviors`);
        },
      },

      automationScopes: {
        index() {
          return axios.get(`${window.api_url}/organization/automations/automation_scopes`);
        },
      },

      fieldMapping: asyncApi({
        endpoint: '/organization/automations/field_mappings',
        payloadKey: 'field_mapping',
      }),

      step: asyncApi({
        endpoint: '/organization/automations/steps',
        payloadKey: 'step',
      }),

      scheduledAutomation: asyncApi({
        endpoint: '/organization/automations/scheduled_automations',
      }),

      statistics: asyncApi({
        endpoint: '/organization/automations/statistics',
      }),

      stepLog: {
        baseUrl(automationId, id = null) {
          let url = `${window.api_url}/organization/automations/${automationId}/step_logs`;
          if (id) url += `/${id}`;
          return url;
        },

        async index(automationId, params = {}, successCallback = null, failureCallback = null) {
          return axios
            .get(this.baseUrl(automationId), { params })
            .then((response) => {
              if (successCallback) successCallback(response);
              return response;
            })
            .catch((error) => {
              if (failureCallback) return failureCallback(error);
              return handleError(error);
            });
        },

        async get(automationId, id, params = {}, successCallback = null, failureCallback = null) {
          return axios
            .get(this.baseUrl(automationId, id), { params })
            .then((response) => {
              if (successCallback) successCallback(response);
              return response;
            })
            .catch((error) => {
              if (failureCallback) return failureCallback(error);
              return handleError(error);
            });
        },
      },
    },

    worksheet: {
      baseUrl(id = null) {
        let url = `${window.api_url}/organization/worksheets`;
        if (id) url += `/${id}`;
        return url;
      },

      create(resource, success, failure) {
        axios.post(this.baseUrl(), { worksheet: resource }).then(success).catch(failure);
      },

      destroy(id, success) {
        axios.delete(this.baseUrl(id)).then(success);
      },

      downloadUrl(id, filename) {
        return `${this.baseUrl(id)}.pdf?filename=${encodeURIComponent(filename)}.pdf`;
      },

      index(filters, success) {
        axios.get(this.baseUrl(), { params: filters }).then(success);
      },

      get(id, success) {
        axios.get(this.baseUrl(id)).then(success);
      },

      update(id, resource, success, failure) {
        axios.patch(this.baseUrl(id), { worksheet: resource }).then(success).catch(failure);
      },
    },
  },

  documentation: {
    ...asyncApi({ endpoint: '/documentation' }),
    async pages(view) {
      const params = view ? { view } : {};
      return axios.get(`${window.api_url}/documentation/pages`, { params });
    },
    pdfUrl(path) {
      return `${window.api_url}/documentation/${path}.pdf`;
    },
    async downloadPdf(path, filename, params = {}) {
      const url = `${window.api_url}/documentation/${path}.pdf`;
      const response = await axios.get(url, {
        params,
        responseType: 'blob',
        headers: { Accept: 'application/pdf' },
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename || `${(path || '').replace(/\//g, '-')}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    },
  },
};

export default deepmerge.all([baseApi, organizationApi]);
